// ============================================================
// useWorkspaceSignals — real data for the workspace
// notification/intelligence rail.
//
// Aggregates three REAL, read-only sources into rail items:
//   • activation verdict   (api.activation.status — admin-gated)
//   • blocked post jobs    (api.commandCenter.jobs.list state=blocked)
//   • olivium receipts     (api.commandCenter.receipts)
//
// Honesty contract:
//   • GET-only — no provider write ever fires from a page load;
//   • each source fails closed independently (401/403/network →
//     the source is reported unavailable, never faked);
//   • sourceLabel names exactly which sources are live so the
//     rail can tell the truth about where signal comes from.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, isAuthError, isForbiddenError } from '../api/client'
import type { ActivationReport, CommandCenterReceipt, PostJob } from '../api/client'
import { SHELL_TOKENS, timeAgo } from '../shell/styles'

export interface WorkspaceSignalItem {
  id: string
  channel: 'system' | 'approval' | 'agent' | 'receipt' | 'warning'
  title: string
  body?: string
  time: string
  isNew?: boolean
  urgent?: boolean
  href?: string
  onOpen?: () => void
}

export interface WorkspaceSignalChannel {
  key: string
  label: string
  accent: string
  glyph: string
}

export const WORKSPACE_SIGNAL_CHANNELS: WorkspaceSignalChannel[] = [
  { key: 'system', label: 'System', accent: SHELL_TOKENS.cyan, glyph: '◆' },
  { key: 'warning', label: 'Warnings', accent: SHELL_TOKENS.red, glyph: '⚑' },
  { key: 'approval', label: 'Approvals', accent: SHELL_TOKENS.purple, glyph: '⛨' },
  { key: 'agent', label: 'Agents', accent: SHELL_TOKENS.green, glyph: '⬡' },
  { key: 'receipt', label: 'Receipts', accent: SHELL_TOKENS.gold, glyph: '▢' },
]

interface SourceState {
  activation: 'live' | 'unavailable' | 'pending'
  jobs: 'live' | 'unavailable' | 'pending'
  receipts: 'live' | 'unavailable' | 'pending'
}

const RECENT_WINDOW_MS = 30 * 60 * 1000

function isRecent(iso: string | null | undefined): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  return !isNaN(t) && Date.now() - t < RECENT_WINDOW_MS
}

export function useWorkspaceSignals(options?: { limit?: number }) {
  const limit = options?.limit ?? 12
  const navigate = useNavigate()
  const [items, setItems] = useState<WorkspaceSignalItem[]>([])
  const [loading, setLoading] = useState(true)
  const [sources, setSources] = useState<SourceState>({
    activation: 'pending',
    jobs: 'pending',
    receipts: 'pending',
  })

  const refresh = useCallback(async () => {
    setLoading(true)
    const next: WorkspaceSignalItem[] = []
    const state: SourceState = { activation: 'pending', jobs: 'pending', receipts: 'pending' }

    // Every source is independent and fails closed on its own.
    const [activation, jobs, receipts] = await Promise.allSettled([
      api.activation.status(),
      api.commandCenter.jobs.list({ state: 'blocked' }),
      api.commandCenter.receipts({ limit }),
    ])

    if (activation.status === 'fulfilled') {
      state.activation = 'live'
      const report: ActivationReport = activation.value.activation
      const blockedCheck = report.verdict !== 'ready'
      next.push({
        id: 'activation-verdict',
        channel: blockedCheck ? 'warning' : 'system',
        title:
          report.verdict === 'ready'
            ? `Activation Doctor ready — ${report.readiness_percent}% of checks pass`
            : report.verdict === 'attention'
              ? `Activation needs attention — ${report.summary.warn} check(s) want a look`
              : `Activation blocked — ${report.summary.fail} check(s) failing`,
        time: timeAgo(report.generated_at),
        urgent: report.verdict === 'blocked',
        isNew: report.verdict !== 'ready',
        href: '/square/command-center/agents',
      })
    } else {
      state.activation = 'unavailable'
    }

    if (jobs.status === 'fulfilled') {
      state.jobs = 'live'
      const blocked: PostJob[] = jobs.value.jobs || []
      for (const job of blocked.slice(0, 5)) {
        next.push({
          id: `job-${job.id}`,
          channel: 'approval',
          title: `Job blocked at gate — ${job.title}`,
          body: job.approval_gate ? `gate: ${job.approval_gate}` : undefined,
          time: timeAgo(job.updated_at || job.created_at),
          urgent: true,
          isNew: isRecent(job.updated_at || job.created_at),
          href: '/square/command-center/operations',
        })
      }
    } else {
      state.jobs = 'unavailable'
    }

    if (receipts.status === 'fulfilled') {
      state.receipts = 'live'
      const rows: CommandCenterReceipt[] = receipts.value.receipts || []
      for (const r of rows.slice(0, limit)) {
        const critical = r.severity === 'critical' || r.severity === 'error'
        next.push({
          id: `receipt-${r.id}`,
          channel: critical ? 'warning' : r.action.startsWith('agent') ? 'agent' : 'receipt',
          title: `${r.action.replace(/[._]/g, ' ')} — ${r.resource_type}`,
          body: r.outcome && r.outcome !== 'success' ? `outcome: ${r.outcome}` : undefined,
          time: timeAgo(r.created_at),
          urgent: critical,
          isNew: isRecent(r.created_at),
          href: '/square/olivium/receipts',
        })
      }
    } else {
      state.receipts = 'unavailable'
    }

    // Auth failures mean the whole session lacks access — say so once.
    const allAuthFailed = [activation, jobs, receipts].every(
      (r) => r.status === 'rejected' && (isAuthError(r.reason) || isForbiddenError(r.reason)),
    )
    if (allAuthFailed) {
      next.push({
        id: 'signals-auth',
        channel: 'warning',
        title: 'Sign in to load live signal — sources are operator-gated.',
        time: '—',
        href: '/auth/login',
      })
    }

    setItems(next)
    setSources(state)
    setLoading(false)
  }, [limit])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const withNavigation = useMemo(
    () =>
      items.map((item) =>
        item.href ? { ...item, onOpen: () => navigate(item.href as string) } : item,
      ),
    [items, navigate],
  )

  const sourceLabel = useMemo(() => {
    const live = Object.entries(sources).filter(([, v]) => v === 'live').map(([k]) => k)
    const down = Object.entries(sources).filter(([, v]) => v === 'unavailable').map(([k]) => k)
    if (live.length === 0 && down.length === 0) return 'loading sources…'
    const parts: string[] = []
    if (live.length > 0) parts.push(`live: ${live.join(' · ')}`)
    if (down.length > 0) parts.push(`unavailable: ${down.join(' · ')}`)
    return parts.join('   ')
  }, [sources])

  return {
    items: withNavigation,
    channels: WORKSPACE_SIGNAL_CHANNELS,
    loading,
    sourceLabel,
    live: Object.values(sources).some((v) => v === 'live'),
    refresh,
  }
}
